import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from "@mui/material";
import EditIcon from '~/icons/svg/EditIcon.svg?react';
import DeleteIcon from '~/icons/svg/DeleteIcon.svg?react';
// import PDFIcon from '~/icons/svg/PDFIcon.svg?react';
import SeemoreIcon from '~/icons/svg/SeeMoreIcon.svg?react';
import {type MouseEvent, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useDeleteSessionMutation} from "../../hooks/useDeleteSessionMutation";
import {supersetDashboardsQueryKey} from "../../hooks/useSupersetDashboards";
import {useChatContext} from "../../context/ChatContext";
import {useStores} from "../../../../stores";
import {notificationStore} from "../../../../stores/init";


export function ActionsMenu({
                                onRenameSession,
                                sessionId,
                                sessionName,
                                appName,
                                hasDashboard = false,
                            }: {
                                onRenameSession: () => void;
                                sessionId: string;
                                sessionName: string;
                                appName: string;
                                /** When true, deleting this chat also refreshes the dashboards list. */
                                hasDashboard?: boolean;
                            }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const open = Boolean(anchorEl);


    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    function handleRenameSession() {
        onRenameSession();
        handleClose();
    }

    function handleDeleteClick() {
        handleClose();
        setDeleteDialogOpen(true);
    }


    function handleDeleteDialogClose() {
        setDeleteDialogOpen(false);
    }


    return (
        <>
            <IconButton onClick={handleClick}>
                <SeemoreIcon width={18} height={18} style={{transform: 'rotate(90deg)'}}/>
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose} disableRestoreFocus component="ul">
                <MenuItem onClick={handleRenameSession}>
                    <ListItemIcon>
                        <EditIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Rename</ListItemText>
                </MenuItem>

                {/*<MenuItem>*/}
                {/*    <ListItemIcon>*/}
                {/*        <PDFIcon fontSize="small"/>*/}
                {/*    </ListItemIcon>*/}
                {/*    <ListItemText>Download PDF</ListItemText>*/}
                {/*</MenuItem>*/}

                <MenuItem onClick={handleDeleteClick}>
                    <ListItemIcon>
                        <DeleteIcon color="#BA1A1A" fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>

            <DeleteSessionModal
                deleteDialogOpen={deleteDialogOpen}
                onDialogClose={handleDeleteDialogClose}
                sessionId={sessionId}
                sessionName={sessionName}
                appName={appName}
                hasDashboard={hasDashboard}
            />
        </>
    );
}


function DeleteSessionModal({deleteDialogOpen, sessionId, sessionName, appName, hasDashboard, onDialogClose}: {
    deleteDialogOpen: boolean,
    sessionId: string,
    sessionName: string,
    appName: string,
    hasDashboard: boolean,
    onDialogClose: () => void
}) {
    const {mutate: deleteSession, isPending: isDeleting} = useDeleteSessionMutation();
    const {removeSession} = useChatContext();
    const {bagStore} = useStores();
    const queryClient = useQueryClient();

    function handleConfirmDelete() {
        deleteSession(
            {sessionId, appName},
            {
                onSuccess: () => {
                    removeSession(sessionId);
                    if (hasDashboard && bagStore.bagId) {
                        void queryClient.invalidateQueries({
                            queryKey: supersetDashboardsQueryKey(bagStore.bagId),
                        });
                    }
                    notificationStore.show("Chat deleted successfully.", {
                        severity: "success",
                        id: `chat-delete-success-${sessionId}`,
                        autoHideDuration: 3000,
                    });
                    onDialogClose();
                },
                onError: () => {
                    notificationStore.show("Could not delete chat. Please try again.", {
                        severity: "error",
                        id: `chat-delete-error-${sessionId}`,
                        autoHideDuration: 3000,
                    });
                },
            },
        );
    }

    function handleDeleteDialogClose() {
        if (isDeleting) return;
        onDialogClose();
    }

    return (
        <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="xs" fullWidth>
            <DialogTitle>Delete chat?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {sessionName
                        ? `"${sessionName}" will be permanently deleted. This can't be undone.`
                        : "This chat will be permanently deleted. This can't be undone."}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={handleDeleteDialogClose} disabled={isDeleting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirmDelete}
                    color="error"
                    variant="contained"
                    sx={{color: '#FFF'}}
                    disabled={isDeleting}
                    loading={isDeleting}
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
}